import { useEffect, useRef, type ReactNode } from "react";
import { useUiStore } from "../store/uiStore";
import type { Toast, ToastType } from "../store/uiStore";
import { CheckCircle, AlertTriangle, CircleAlert, Info, X } from "lucide-react";

const typeConfig: Record<ToastType, { solidBg: string; icon: typeof Info }> = {
  success: { solidBg: "bg-success", icon: CheckCircle },
  error: { solidBg: "bg-danger", icon: CircleAlert },
  warning: { solidBg: "bg-warning", icon: AlertTriangle },
  info: { solidBg: "bg-info", icon: Info },
};

/**
 * Reusable inline alert banner. Use directly in page content for static messages.
 *
 * Usage:
 *   <Alert type="info" message="Helpful information here" />
 *   <Alert type="warning" message="Watch out!" dismissable onDismiss={() => setShow(false)} />
 *   <Alert type="error" message="Something went wrong" />
 */
const tintConfig: Record<ToastType, { bg: string; text: string; iconColor: string }> = {
  success: { bg: "bg-success-bg", text: "text-gray-800", iconColor: "text-success" },
  error: { bg: "bg-danger-bg", text: "text-gray-800", iconColor: "text-danger" },
  warning: { bg: "bg-warning-bg", text: "text-gray-800", iconColor: "text-warning" },
  info: { bg: "bg-info-bg", text: "text-gray-800", iconColor: "text-info" },
};

interface AlertProps {
  type: ToastType;
  message: ReactNode;
  /** "solid" = colored bg + white text (toast-like). "tint" = light bg + dark text (inline callout). Default: "tint". */
  variant?: "solid" | "tint";
  showIcon?: boolean;
  dismissable?: boolean;
  onDismiss?: () => void;
  className?: string;
}

export function Alert({ type, message, variant = "tint", showIcon = type !== "info", dismissable = false, onDismiss, className }: AlertProps) {
  const config = typeConfig[type];
  const tint = tintConfig[type];
  const Icon = config.icon;
  const isSolid = variant === "solid";

  return (
    <div
      className={`rounded-lg px-4 py-3 text-sm section-reveal ${isSolid ? `${config.solidBg} text-white` : `${tint.bg} ${tint.text}`} ${className ?? ""}`}
      role="alert"
    >
      <div className="flex items-center gap-3">
        {showIcon && <Icon size={22} className={`shrink-0 ${isSolid ? "text-white/80" : tint.iconColor}`} aria-hidden="true" />}
        <span className="flex-1">{message}</span>
        {dismissable && (
          <button
            onClick={onDismiss}
            className={`shrink-0 transition-colors cursor-pointer ${isSolid ? "text-white/60 hover:text-white" : "text-gray-400 hover:text-gray-600"}`}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

function ToastItem({ toast, dismissToast }: { toast: Toast; dismissToast: (id: string) => void }) {
  const config = typeConfig[toast.type];
  const Icon = config.icon;
  const ref = useRef<HTMLDivElement>(null);
  const hasActions = !!toast.actions && toast.actions.length > 0;

  // Focus once when this toast first mounts so keyboard users land on it.
  // Intentionally no dependency on toast.exiting — re-focusing during the
  // exit animation would steal focus from whatever the user moved on to.
  useEffect(() => {
    if (!toast.exiting) ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={ref}
      tabIndex={-1}
      className={`rounded-xl text-sm text-white overflow-hidden relative ${config.solidBg} ${
        toast.exiting ? "toast-exit" : "toast-enter"
      } shadow-[0_4px_24px_rgba(0,0,0,0.15)] focus:outline-none`}
      role="alertdialog"
      aria-label={toast.message}
    >
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3">
        <Icon size={24} className="shrink-0 text-white" aria-hidden="true" />
        <span className="flex-1 min-w-0">{toast.message}</span>
        {hasActions ? (
          <div className="flex flex-wrap gap-2 shrink-0" role="group" aria-label="Toast actions">
            {toast.actions!.map((action) => (
              <button
                key={action.label}
                onClick={() => {
                  dismissToast(toast.id);
                  action.onClick?.();
                }}
                className={`text-sm px-4 py-1.5 rounded-lg transition-colors cursor-pointer font-medium focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  action.primary
                    ? "bg-white text-gray-800 hover:bg-white/90"
                    : "border border-white/40 text-white hover:bg-white/10"
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>
        ) : toast.dismissable ? (
          <button
            onClick={() => dismissToast(toast.id)}
            className="shrink-0 text-white/60 hover:text-white transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Toast container — renders floating alerts from the uiStore.
 * Toasts support actions (buttons) and configurable dismissability.
 */
export function ToastContainer() {
  const toasts = useUiStore((s) => s.toasts);
  const dismissToast = useUiStore((s) => s.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-28 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 w-full max-w-2xl px-3 sm:px-4" aria-live="polite">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} dismissToast={dismissToast} />
      ))}
    </div>
  );
}
